import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { mockProjects, createDefaultProject } from '../data/mockProjects';
import { v4 as uuidv4 } from 'uuid';

const ProjectContext = createContext();

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

// Demo-Kunden mit Gebäuden
const initialCustomers = [
  {
    id: 'cust-1',
    name: 'Müller GmbH',
    ansprechpartner: 'Hans Müller',
    strasse: 'Industriestr. 15',
    plz: '80995',
    ort: 'München',
    telefon: '089 12345678',
    email: 'info@mueller-gmbh.de',
    buildings: [
      { id: 'build-1', name: 'Hauptgebäude', address: 'Industriestr. 15, 80995 München' },
      { id: 'build-2', name: 'Lagerhalle', address: 'Industriestr. 17, 80995 München' }
    ]
  },
  {
    id: 'cust-2',
    name: 'Schmidt Wohnbau',
    ansprechpartner: 'Maria Schmidt',
    strasse: 'Bahnhofstr. 42',
    plz: '81541',
    ort: 'München',
    telefon: '089 98765432',
    email: 'kontakt@schmidt-wohnbau.de',
    buildings: [
      { id: 'build-3', name: 'Mehrfamilienhaus A', address: 'Rosenweg 5, 81541 München' }
    ]
  }
];

// LocalStorage-Hilfsfunktionen
const loadFromStorage = (key, fallback) => {
  try {
    const stored = localStorage.getItem(key);
    if (stored && stored !== 'undefined' && stored !== 'null') {
      const parsed = JSON.parse(stored);
      // Validiere dass es ein nicht-leeres Array oder Objekt ist
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
      if (typeof parsed === 'object' && parsed !== null && Object.keys(parsed).length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.error(`Fehler beim Laden von ${key}:`, e);
  }
  return fallback;
};

const saveToStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.error(`Fehler beim Speichern von ${key}:`, e);
  }
};

// Speziell für einfache Strings (wie Project-IDs)
const saveIdToStorage = (key, id) => {
  try {
    localStorage.setItem(key, id); // Keine JSON.stringify für einfache Strings
  } catch (e) {
    console.error(`Fehler beim Speichern von ${key}:`, e);
  }
};

const loadIdFromStorage = (key) => {
  try {
    return localStorage.getItem(key); // Gibt direkt den String zurück
  } catch (e) {
    console.error(`Fehler beim Laden von ${key}:`, e);
  }
  return null;
};


export const ProjectProvider = ({ children }) => {
  // Initialisierung aus localStorage oder Fallback
  const [projects, setProjects] = useState(() => 
    loadFromStorage('stromlaufplan_projects', mockProjects)
  );
  const [currentProject, setCurrentProject] = useState(() => {
    const storedId = loadIdFromStorage('stromlaufplan_currentProjectId');
    const loadedProjects = loadFromStorage('stromlaufplan_projects', mockProjects);
    if (storedId) {
      const found = loadedProjects.find(p => p.id === storedId);
      if (found) return found;
    }
    return loadedProjects[0] || null;
  });
  const [selectedNode, setSelectedNode] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set(['node-1', 'node-2', 'node-3', 'node-4', 'node-5', 'node-12']));
  
  // Kunden und Gebäude
  const [customers, setCustomers] = useState(() => 
    loadFromStorage('stromlaufplan_customers', initialCustomers)
  );

  // ================================================================
  // PERSISTENZ - Auto-Save bei Änderungen
  // ================================================================
  useEffect(() => {
    saveToStorage('stromlaufplan_projects', projects);
  }, [projects]);

  useEffect(() => {
    if (currentProject?.id) {
      saveIdToStorage('stromlaufplan_currentProjectId', currentProject.id);
    }
  }, [currentProject?.id]);

  useEffect(() => {
    saveToStorage('stromlaufplan_customers', customers);
  }, [customers]);

  // ================================================================
  // KUNDEN-VERWALTUNG
  // ================================================================
  const addCustomer = useCallback((customerData) => {
    const newCustomer = {
      id: `cust-${Date.now()}`,
      ...customerData,
      buildings: []
    };
    setCustomers(prev => [...prev, newCustomer]);
    return newCustomer;
  }, []);

  const updateCustomer = useCallback((customerId, updates) => {
    setCustomers(prev => prev.map(c => 
      c.id === customerId ? { ...c, ...updates } : c
    ));
  }, []);

  const deleteCustomer = useCallback((customerId) => {
    setCustomers(prev => prev.filter(c => c.id !== customerId));
    // Projekte ohne Kunde belassen (customerId = null)
    setProjects(prev => prev.map(p => 
      p.customerId === customerId ? { ...p, customerId: null, buildingId: null } : p
    ));
  }, []);

  // ================================================================
  // GEBÄUDE-VERWALTUNG
  // ================================================================
  const addBuilding = useCallback((customerId, buildingData) => {
    const newBuilding = {
      id: `build-${Date.now()}`,
      ...buildingData
    };
    setCustomers(prev => prev.map(c => 
      c.id === customerId 
        ? { ...c, buildings: [...c.buildings, newBuilding] }
        : c
    ));
    return newBuilding;
  }, []);

  const updateBuilding = useCallback((customerId, buildingId, updates) => {
    setCustomers(prev => prev.map(c => 
      c.id === customerId 
        ? { ...c, buildings: c.buildings.map(b => b.id === buildingId ? { ...b, ...updates } : b) }
        : c
    ));
  }, []);

  const deleteBuilding = useCallback((customerId, buildingId) => {
    setCustomers(prev => prev.map(c => 
      c.id === customerId 
        ? { ...c, buildings: c.buildings.filter(b => b.id !== buildingId) }
        : c
    ));
    // Projekte ohne Gebäude belassen
    setProjects(prev => prev.map(p => 
      p.buildingId === buildingId ? { ...p, buildingId: null } : p
    ));
  }, []);

  // Projekte nach Kunde/Gebäude filtern
  const getProjectsByCustomer = useCallback((customerId) => {
    return projects.filter(p => p.customerId === customerId);
  }, [projects]);

  const getProjectsByBuilding = useCallback((buildingId) => {
    return projects.filter(p => p.buildingId === buildingId);
  }, [projects]);

  // Importiertes Projekt direkt hinzufügen (mit allen Daten)
  const addImportedProject = useCallback((projectData) => {
    const newProject = {
      id: `imported-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      ...projectData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setProjects(prev => [...prev, newProject]);
    setCurrentProject(newProject);
    return newProject;
  }, []);

  // Projekt erstellen (optional mit Kunde/Gebäude)
  const createProject = useCallback((name, address, customerId = null, buildingId = null) => {
    const newProject = {
      ...createDefaultProject(),
      name: name || 'Neues Projekt',
      address: address || '',
      customerId,
      buildingId
    };
    setProjects(prev => [...prev, newProject]);
    setCurrentProject(newProject);
    return newProject;
  }, []);

  // Projekt auswählen
  const selectProject = useCallback((projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setCurrentProject(project);
      setSelectedNode(null);
    }
  }, [projects]);

  // Projekt aktualisieren
  const updateProject = useCallback((projectId, updates) => {
    setProjects(prev => prev.map(p => 
      p.id === projectId ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p
    ));
    if (currentProject?.id === projectId) {
      setCurrentProject(prev => ({ ...prev, ...updates, updatedAt: new Date().toISOString() }));
    }
  }, [currentProject]);

  // Projekt löschen
  const deleteProject = useCallback((projectId) => {
    setProjects(prev => prev.filter(p => p.id !== projectId));
    if (currentProject?.id === projectId) {
      setCurrentProject(projects.find(p => p.id !== projectId) || null);
    }
  }, [currentProject, projects]);

  // Hilfsfunktion: Knoten rekursiv finden
  const findNodeById = useCallback((nodes, nodeId) => {
    for (const node of nodes) {
      if (node.id === nodeId) return node;
      if (node.children?.length > 0) {
        const found = findNodeById(node.children, nodeId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Hilfsfunktion: Parent-Knoten finden
  const findParentNode = useCallback((nodes, nodeId, parent = null) => {
    for (const node of nodes) {
      if (node.id === nodeId) return parent;
      if (node.children?.length > 0) {
        const found = findParentNode(node.children, nodeId, node);
        if (found !== undefined) return found;
      }
    }
    return undefined;
  }, []);

  // Knoten hinzufügen
  const addNode = useCallback((parentId, nodeType, data = {}) => {
    const newNode = {
      id: uuidv4(),
      type: nodeType,
      bezeichnung: data.bezeichnung || `Neuer ${nodeType}`,
      expanded: false,
      data: data,
      children: []
    };

    const addToParent = (nodes) => {
      return nodes.map(node => {
        if (node.id === parentId) {
          return {
            ...node,
            expanded: true,
            children: [...(node.children || []), newNode]
          };
        }
        if (node.children?.length > 0) {
          return {
            ...node,
            children: addToParent(node.children)
          };
        }
        return node;
      });
    };

    if (parentId === null) {
      // Auf Root-Ebene hinzufügen
      updateProject(currentProject.id, {
        nodes: [...currentProject.nodes, newNode]
      });
    } else {
      updateProject(currentProject.id, {
        nodes: addToParent(currentProject.nodes)
      });
    }

    setExpandedNodes(prev => new Set([...prev, parentId]));
    setSelectedNode(newNode);
    return newNode;
  }, [currentProject, updateProject]);

  // Knoten aktualisieren
  const updateNode = useCallback((nodeId, updates) => {
    const updateInTree = (nodes) => {
      return nodes.map(node => {
        if (node.id === nodeId) {
          const updatedNode = {
            ...node,
            ...updates,
            data: { ...node.data, ...updates.data }
          };
          if (selectedNode?.id === nodeId) {
            setSelectedNode(updatedNode);
          }
          return updatedNode;
        }
        if (node.children?.length > 0) {
          return {
            ...node,
            children: updateInTree(node.children)
          };
        }
        return node;
      });
    };

    updateProject(currentProject.id, {
      nodes: updateInTree(currentProject.nodes)
    });
  }, [currentProject, selectedNode, updateProject]);

  // Knoten löschen
  const deleteNode = useCallback((nodeId) => {
    const removeFromTree = (nodes) => {
      return nodes.filter(node => node.id !== nodeId).map(node => {
        if (node.children?.length > 0) {
          return {
            ...node,
            children: removeFromTree(node.children)
          };
        }
        return node;
      });
    };

    updateProject(currentProject.id, {
      nodes: removeFromTree(currentProject.nodes)
    });

    if (selectedNode?.id === nodeId) {
      setSelectedNode(null);
    }
  }, [currentProject, selectedNode, updateProject]);

  // Knoten verschieben
  const moveNode = useCallback((nodeId, newParentId, position = -1) => {
    let movedNode = null;

    // Knoten aus dem Baum entfernen und speichern
    const removeAndSave = (nodes) => {
      return nodes.filter(node => {
        if (node.id === nodeId) {
          movedNode = { ...node };
          return false;
        }
        return true;
      }).map(node => {
        if (node.children?.length > 0) {
          return {
            ...node,
            children: removeAndSave(node.children)
          };
        }
        return node;
      });
    };

    // BMK für den neuen Parent generieren
    const generateBMK = (parentNode, siblings, position) => {
      // Ermittle den Basis-BMK des Parents
      let baseBMK = '';
      if (parentNode) {
        const parentBez = parentNode.bezeichnung || '';
        const match = parentBez.match(/^([A-Z]\d+(?:\.\d+)*)/);
        if (match) {
          baseBMK = match[1];
        }
      }
      
      // Wenn kein Parent oder kein BMK, generiere einen neuen
      if (!baseBMK) {
        // Finde den höchsten F-Index auf Root-Ebene
        const rootFNumbers = siblings
          .map(n => {
            const m = (n.bezeichnung || '').match(/^F(\d+)/);
            return m ? parseInt(m[1]) : 0;
          })
          .filter(n => !isNaN(n));
        const maxF = Math.max(0, ...rootFNumbers);
        return `F${maxF + 1}`;
      }
      
      // Generiere nächsten Unter-BMK
      const childBMKs = siblings
        .map(n => {
          const m = (n.bezeichnung || '').match(new RegExp(`^${baseBMK}\\.(\\d+)`));
          return m ? parseInt(m[1]) : 0;
        })
        .filter(n => !isNaN(n));
      const maxChild = Math.max(0, ...childBMKs);
      return `${baseBMK}.${maxChild + 1}`;
    };

    // Knoten an neuer Stelle einfügen
    const insertNode = (nodes) => {
      if (newParentId === null) {
        // Auf Root-Ebene einfügen
        if (position === -1) {
          return [...nodes, movedNode];
        }
        const newNodes = [...nodes];
        newNodes.splice(position, 0, movedNode);
        return newNodes;
      }

      return nodes.map(node => {
        if (node.id === newParentId) {
          const children = [...(node.children || [])];
          
          // Generiere neuen BMK basierend auf Parent
          const newBMK = generateBMK(node, children, position);
          const oldBez = movedNode.bezeichnung || '';
          const namePart = oldBez.includes(' - ') ? oldBez.split(' - ')[1] : '';
          movedNode = {
            ...movedNode,
            bezeichnung: namePart ? `${newBMK} - ${namePart}` : newBMK
          };
          
          if (position === -1) {
            children.push(movedNode);
          } else {
            children.splice(position, 0, movedNode);
          }
          return { ...node, children, expanded: true };
        }
        if (node.children?.length > 0) {
          return { ...node, children: insertNode(node.children) };
        }
        return node;
      });
    };

    const nodesWithoutMoved = removeAndSave(currentProject.nodes);
    if (movedNode) {
      const newNodes = newParentId === null && position === -1
        ? [...nodesWithoutMoved, movedNode]
        : insertNode(nodesWithoutMoved);
      updateProject(currentProject.id, { nodes: newNodes });
    }
  }, [currentProject, updateProject]);

  // Knoten VOR einem anderen Knoten einfügen (auf gleicher Ebene)
  const moveNodeBefore = useCallback((nodeId, targetNodeId) => {
    const targetParent = findParentNode(currentProject?.nodes || [], targetNodeId);
    const siblings = targetParent ? targetParent.children : currentProject?.nodes || [];
    const targetIndex = siblings.findIndex(n => n.id === targetNodeId);
    
    if (targetIndex !== -1) {
      const parentId = targetParent?.id || null;
      moveNode(nodeId, parentId, targetIndex);
    }
  }, [currentProject, findParentNode, moveNode]);

  // Knoten expandieren/kollabieren
  const toggleNodeExpanded = useCallback((nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  // Alle Knoten expandieren
  const expandAll = useCallback(() => {
    const getAllNodeIds = (nodes) => {
      let ids = [];
      for (const node of nodes) {
        ids.push(node.id);
        if (node.children?.length > 0) {
          ids = [...ids, ...getAllNodeIds(node.children)];
        }
      }
      return ids;
    };
    setExpandedNodes(new Set(getAllNodeIds(currentProject?.nodes || [])));
  }, [currentProject]);

  // Alle Knoten kollabieren
  const collapseAll = useCallback(() => {
    setExpandedNodes(new Set());
  }, []);

  // Knoten duplizieren (inkl. aller Kinder)
  const duplicateNode = useCallback((nodeId) => {
    const nodeToDuplicate = findNodeById(currentProject?.nodes || [], nodeId);
    if (!nodeToDuplicate) return null;
    
    // Rekursiv neue IDs generieren
    const cloneWithNewIds = (node) => {
      const newNode = {
        ...node,
        id: uuidv4(),
        bezeichnung: `${node.bezeichnung} (Kopie)`,
        data: { ...node.data },
        children: node.children?.map(child => cloneWithNewIds(child)) || []
      };
      return newNode;
    };
    
    const duplicatedNode = cloneWithNewIds(nodeToDuplicate);
    
    // Parent finden und dort einfügen
    const parent = findParentNode(currentProject?.nodes || [], nodeId);
    
    if (parent === null) {
      // Root-Level Node
      updateProject(currentProject.id, {
        nodes: [...currentProject.nodes, duplicatedNode]
      });
    } else if (parent) {
      // Node im Parent einfügen
      const insertAfterOriginal = (nodes) => {
        return nodes.flatMap(node => {
          if (node.id === nodeId) {
            return [node, duplicatedNode];
          }
          if (node.children?.length > 0) {
            return [{
              ...node,
              children: insertAfterOriginal(node.children)
            }];
          }
          return [node];
        });
      };
      
      updateProject(currentProject.id, {
        nodes: insertAfterOriginal(currentProject.nodes)
      });
    }
    
    setSelectedNode(duplicatedNode);
    return duplicatedNode;
  }, [currentProject, findNodeById, findParentNode, updateProject]);

  const value = {
    projects,
    currentProject,
    selectedNode,
    expandedNodes,
    setSelectedNode,
    createProject,
    addImportedProject,
    selectProject,
    updateProject,
    deleteProject,
    addNode,
    updateNode,
    deleteNode,
    moveNode,
    moveNodeBefore,
    toggleNodeExpanded,
    expandAll,
    collapseAll,
    duplicateNode,
    findNodeById: (nodeId) => findNodeById(currentProject?.nodes || [], nodeId),
    findParentNode: (nodeId) => findParentNode(currentProject?.nodes || [], nodeId),
    // Kunden & Gebäude
    customers,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    addBuilding,
    updateBuilding,
    deleteBuilding,
    getProjectsByCustomer,
    getProjectsByBuilding
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export default ProjectContext;
